import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User, DollarSign, TrendingUp, TrendingDown } from "lucide-react";
import SyncIndicator from "@/components/SyncIndicator";

import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { usePagination } from "../hooks/usePagination";
import TablePagination from "../components/TablePagination";
import { formatRupees } from "../utils/currency";

import PropTypes from 'prop-types';

export default function ReferralDoctorSummary({ onLogout }) {
    const navigate = useNavigate();
    const { doctorId } = useParams();
    const currentDoctorId = localStorage.getItem('user_id');

    const data = useLiveQuery(async () => {
        if (!doctorId) return null;

        const doctor = await db.users.get(doctorId);
        if (!doctor) return null;

        // Get all facility_charges for this referral doctor
        const facility_charges = await db.facility_charges.where('associated_doctor_id').equals(doctorId).toArray();

        // Sort by created_at descending
        facility_charges.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        // Get appointment info for each facility_charge
        const appointmentIds = facility_charges.map(c => c.appointment_id).filter(Boolean);
        const appointments = await db.appointments.where('id').anyOf(appointmentIds).toArray();
        const appointmentMap = new Map(appointments.map(a => [a.id, a]));

        // Enrich facility_charges with appointment data
        const enrichedCharges = facility_charges.map(c => ({
            ...c,
            appointment: appointmentMap.get(c.appointment_id)
        }));

        return { doctor, facility_charges: enrichedCharges };
    }, [doctorId]);

    // Calculate current month totals
    const monthlyStats = useMemo(() => {
        if (!data?.facility_charges) return { total: 0, pending: 0, paid: 0, count: 0 };

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const thisMonthCharges = data.facility_charges.filter(c => {
            const date = new Date(c.created_at);
            return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
        });

        const total = thisMonthCharges.reduce((sum, c) => sum + c.charge_amount, 0);
        const pending = thisMonthCharges.filter(c => c.paid_status === 'pending').reduce((sum, c) => sum + c.charge_amount, 0);
        const paid = thisMonthCharges.filter(c => c.paid_status === 'paid').reduce((sum, c) => sum + c.charge_amount, 0);

        return { total, pending, paid, count: thisMonthCharges.length };
    }, [data?.facility_charges]);

    // All-time totals
    const allTimeStats = useMemo(() => {
        if (!data?.facility_charges) return { total: 0, pending: 0, paid: 0 };

        const total = data.facility_charges.reduce((sum, c) => sum + c.charge_amount, 0);
        const pending = data.facility_charges.filter(c => c.paid_status === 'pending').reduce((sum, c) => sum + c.charge_amount, 0);
        const paid = data.facility_charges.filter(c => c.paid_status === 'paid').reduce((sum, c) => sum + c.charge_amount, 0);

        return { total, pending, paid };
    }, [data?.facility_charges]);

    // Determine if current user is paying or receiving
    const isPayer = currentDoctorId !== doctorId;

    const pagination = usePagination(data?.facility_charges || [], {
        initialPageSize: 10,
        isMobile: window.innerWidth < 768
    });

    if (!data) {
        return (
            <div className="page-container">
                <Sidebar active="referrals" onLogout={onLogout} />
                <div className="main-content">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                        <Button variant="ghost" onClick={() => navigate('/referrals')}>
                            <ArrowLeft size={20} />
                        </Button>
                        <h1>Loading...</h1>
                    </div>
                </div>
            </div>
        );
    }

    const { doctor, facility_charges } = data;
    const monthName = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

    return (
        <div className="page-container" data-testid="referral-summary-page">
            <Sidebar active="referrals" onLogout={onLogout} />
            <div className="main-content">
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                    <Button variant="ghost" onClick={() => navigate('/referrals')} data-testid="back-button">
                        <ArrowLeft size={20} />
                    </Button>
                    <h1>Associated Doctor Summary</h1>
                </div>

                {/* Doctor Info Card */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{
                            width: '64px',
                            height: '64px',
                            background: '#dbeafe',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <User size={32} color="#1e40af" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <h2 style={{ margin: 0, marginBottom: '0.25rem' }}>{doctor.name}</h2>
                            <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
                                📞 {doctor.phone}
                                {doctor.email && !doctor.email.includes('placeholder') && (
                                    <span style={{ marginLeft: '1rem' }}>✉️ {doctor.email}</span>
                                )}
                            </div>
                            {(doctor.qualification || doctor.city) && (
                                <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                    {doctor.qualification && <span>{doctor.qualification}</span>}
                                    {doctor.qualification && doctor.city && <span> • </span>}
                                    {doctor.city && <span>{doctor.city}</span>}
                                </div>
                            )}
                        </div>
                        {doctor.is_registered ? (
                            <span className="badge badge-completed">Registered</span>
                        ) : (
                            <span className="badge badge-pending">Placeholder</span>
                        )}
                    </div>
                </div>

                {/* Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                    {/* Current Month Summary */}
                    <div className="stat-card" style={{ borderLeft: isPayer ? '4px solid #f59e0b' : '4px solid #10b981' }}>
                        <div className="stat-card-header">
                            <h3>{monthName}</h3>
                            <div className="stat-icon" style={{ background: isPayer ? '#fef3c7' : '#d1fae5' }}>
                                {isPayer ? <TrendingDown size={24} color="#d97706" /> : <TrendingUp size={24} color="#059669" />}
                            </div>
                        </div>
                        <div className="stat-value" data-testid="monthly-total">
                            {formatRupees(monthlyStats.total)}
                        </div>
                        <div className="stat-label">
                            {isPayer ? 'Payable this month' : 'Receivable this month'}
                            {monthlyStats.count > 0 && ` (${monthlyStats.count} records)`}
                        </div>
                    </div>

                    {/* Pending */}
                    <div className="stat-card">
                        <div className="stat-card-header">
                            <h3>Pending</h3>
                            <div className="stat-icon" style={{ background: '#fef3c7' }}>
                                <DollarSign size={24} color="#92400e" />
                            </div>
                        </div>
                        <div className="stat-value" data-testid="total-pending">{formatRupees(allTimeStats.pending)}</div>
                        <div className="stat-label">All-time unpaid</div>
                    </div>

                    {/* Paid */}
                    <div className="stat-card">
                        <div className="stat-card-header">
                            <h3>Settled</h3>
                            <div className="stat-icon" style={{ background: '#d1fae5' }}>
                                <DollarSign size={24} color="#065f46" />
                            </div>
                        </div>
                        <div className="stat-value" data-testid="total-paid">{formatRupees(allTimeStats.paid)}</div>
                        <div className="stat-label">All-time paid</div>
                    </div>
                </div>

                {/* Facility Charge Records */}
                {facility_charges.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon"><DollarSign /></div>
                        <h3>No charge records</h3>
                        <p>Facility charge records for this doctor will appear here</p>
                    </div>
                ) : (
                    <div className="card">
                        <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>
                            Charge Records ({facility_charges.length})
                        </h2>

                        {/* Desktop Table View */}
                        <div className="table-container desktop-only">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Payment Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagination.items.map((comm) => (
                                        <tr
                                            key={comm.id}
                                            data-testid={`facility_charge-row-${comm.id}`}
                                            className={comm._synced === false ? 'entity-row-unsynced' : ''}
                                            onClick={() => navigate(`/facility-charges/${comm.id}`)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    {comm.appointment?.date_time
                                                        ? new Date(comm.appointment.date_time).toLocaleDateString()
                                                        : new Date(comm.created_at).toLocaleDateString()}
                                                    <SyncIndicator show={comm._synced === false} />
                                                </div>
                                            </td>
                                            <td style={{ fontWeight: '600', color: isPayer ? '#d97706' : '#059669' }}>
                                                {formatRupees(comm.charge_amount)}
                                            </td>
                                            <td>
                                                <span className={`badge ${comm.paid_status === 'paid' ? 'badge-paid' : 'badge-pending'}`}>
                                                    {comm.paid_status}
                                                </span>
                                            </td>
                                            <td>{comm.payment_date ? new Date(comm.payment_date).toLocaleDateString() : "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="mobile-only">
                            {pagination.items.map((comm) => (
                                <div
                                    key={comm.id}
                                    data-testid={`facility_charge-card-${comm.id}`}
                                    className={comm._synced === false ? 'entity-card-unsynced' : ''}
                                    onClick={() => navigate(`/facility-charges/${comm.id}`)}
                                    style={{
                                        padding: '1rem',
                                        background: comm._synced === false ? undefined : '#f8fafc',
                                        borderRadius: '8px',
                                        marginBottom: '0.75rem',
                                        borderLeft: `3px solid ${comm.paid_status === 'paid' ? '#10b981' : '#fbbf24'}`,
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span style={{ fontWeight: '500', color: '#64748b' }}>
                                                {comm.appointment?.date_time
                                                    ? new Date(comm.appointment.date_time).toLocaleDateString()
                                                    : new Date(comm.created_at).toLocaleDateString()}
                                            </span>
                                            <SyncIndicator show={comm._synced === false} />
                                        </div>
                                        <span className={`badge ${comm.paid_status === 'paid' ? 'badge-paid' : 'badge-pending'}`}>
                                            {comm.paid_status}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: isPayer ? '#d97706' : '#059669' }}>
                                            {formatRupees(comm.charge_amount)}
                                        </div>
                                        {comm.payment_date && (
                                            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                                Paid: {new Date(comm.payment_date).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <TablePagination
                            pagination={pagination}
                            isMobile={window.innerWidth < 768}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

ReferralDoctorSummary.propTypes = {
    onLogout: PropTypes.func
};
